import React from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
// API
import {
  useValidatePasswordResetTokenQuery,
  useCompletePasswordResetMutation,
} from "src/services/apiQueries/password-reset";
// mantine
import { notifications } from "@mantine/notifications";
import { Text, Title, Box, Button, LoadingOverlay } from "@mantine/core";
// misc ui coponents
import { TanstackMantineLink } from "@components/tanstack-maintine-link";
import { PasswordField } from "@pages/start/components/AccountActivateForm/formComponents/SetPassword/SetPassword";

import {
  PasswordRequirements,
  calculateIfPasswordMeetsRequirements,
  extractErrorMessageFromRequirements,
} from "@pages/start/components/AccountActivateForm/formComponents/SetPassword/password-requirements/password-requirements";

import { usePasswordResetDataStore } from "src/services/password-reset-data-store";

import { PasswordResetCard } from "@components/password-reset-card/password-reset-card";
// form
import { useForm } from "@mantine/form";

const route = getRouteApi("/_passwordreset-layout/reset/$uidb64/set-password");

export const PasswordResetError = React.memo(function PasswordResetError() {
  const { t } = useTranslation();
  return (
    <PasswordResetCard>
      <Title order={2}>{t("passwordReset.newPassword.invalidTitle")}</Title>
      <Text mt="lg">{t("passwordReset.newPassword.invalidCopy")}</Text>

      <Box pt="xl">
        <TanstackMantineLink fw="bold" to="/passwordreset">
          {t("passwordReset.newPassword.invalidLink")}
        </TanstackMantineLink>
      </Box>
    </PasswordResetCard>
  );
});

export const PasswordResetLoading = React.memo(function PasswordResetLoading() {
  return (
    <>
      <LoadingOverlay
        visible
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 3 }}
      />
      <PasswordResetCard />
    </>
  );
});

export const PasswordResetSetPassword = React.memo(
  function PasswordResetSetPassword() {
    const { t } = useTranslation();

    const nav = useNavigate();

    const { uidb64 } = route.useParams();

    const { passwordResetToken: token } = usePasswordResetDataStore();

    // only has a repsonse body if no error raised, where "error" means api return non 2xx status
    const { isFetching } = useValidatePasswordResetTokenQuery(token, uidb64);

    const { mutate, isPending } = useCompletePasswordResetMutation({
      onError: () => {
        notifications.show({
          title: t("passwordReset.newPassword.notifications.errorTitle"),
          message: t("passwordReset.newPassword.notifications.error"),
          color: "red",
        });
      },
      onSuccess: () => {
        nav({ to: "/reset/done" });
      },
    });

    const pathParams = route.useParams();

    const form = useForm<{ password: string; confirmPassword: string }>({
      // NOTE: do not use `mode: "uncontrolled"` here since PasswordRequirements needs the re-render!!
      // this form is not big enough to warrant the performance gains from uncontrolled mode anyways
      initialValues: { password: "", confirmPassword: "" },
      // validation
      validateInputOnBlur: true,
      validateInputOnChange: true,
      validate: {
        password: (value) => {
          const requirements = calculateIfPasswordMeetsRequirements(value, t);
          return extractErrorMessageFromRequirements(requirements);
        },
        confirmPassword: (value, values) => {
          if (!value) {
            return t("startPage.validation.confirmPasswordRequired");
          }
          if (value && value !== values.password) {
            return t("startPage.validation.confirmPasswordMismatch");
          }
        },
      },
    });

    const handleSubmit = form.onSubmit((values) => {
      if (!token) {
        throw new Error("Invalid token");
      }
      return mutate({
        new_password: values.password,
        token,
        uidb64: pathParams.uidb64,
      });
    });

    const shouldDisplayLoading = isPending || isFetching;

    return (
      <PasswordResetCard>
        <LoadingOverlay
          visible={shouldDisplayLoading}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 2 }}
        />

        <Title order={2} data-testid="pw-reset-form-title">
          {t("passwordReset.newPassword.title")}
        </Title>

        <Text mt="lg" data-testid="pw-reset-form-text">
          {t("passwordReset.newPassword.instructions")}
        </Text>

        <Box pt="xl">
          <form onSubmit={handleSubmit}>
            {/* PASSWORD1 */}
            <PasswordField
              {...form.getInputProps("password")}
              showErrorMessage={false}
              data-testid="password-input"
            />
            {/* PASSWORD2 */}
            <PasswordField
              {...form.getInputProps("confirmPassword")}
              showErrorMessage
              data-testid="password-confirm"
              mt="xl"
            />

            <PasswordRequirements
              passwordValue={form.getValues().password}
              shouldDisplayColors={form.isDirty("password")}
            />

            <Button
              type="submit"
              mt="lg"
              data-testid="pw-reset-form-submit"
              disabled={!form.isValid() || shouldDisplayLoading}
            >
              {t("submit")}
            </Button>
          </form>
        </Box>
      </PasswordResetCard>
    );
  },
);
